import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  Clock, 
  Minus,
  AlertCircle,
  DollarSign,
  User,
  FileText,
  CreditCard
} from "lucide-react";

const statusIndicatorVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full font-medium transition-all duration-200",
  {
    variants: {
      variant: {
        success: "bg-[hsl(var(--status-success))]/10 text-[hsl(var(--status-success))] border border-[hsl(var(--status-success))]/20",
        error: "bg-[hsl(var(--status-error))]/10 text-[hsl(var(--status-error))] border border-[hsl(var(--status-error))]/20",
        warning: "bg-[hsl(var(--status-warning))]/10 text-[hsl(var(--status-warning))] border border-[hsl(var(--status-warning))]/20",
        info: "bg-[hsl(var(--status-info))]/10 text-[hsl(var(--status-info))] border border-[hsl(var(--status-info))]/20",
        pending: "bg-muted/50 text-muted-foreground border border-border",
        neutral: "bg-muted text-muted-foreground border border-border",
        brand: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20",
      },
      size: {
        sm: "px-2 py-0.5 text-xs",
        default: "px-2.5 py-1 text-sm",
        lg: "px-3 py-1.5 text-base",
      },
      style: {
        filled: "",
        outline: "bg-transparent",
        ghost: "bg-transparent border-transparent",
        dot: "pl-6 relative",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "default",
      style: "filled",
    },
  }
);

export interface StatusIndicatorProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'style'>,
    VariantProps<typeof statusIndicatorVariants> {
  status?: 'success' | 'error' | 'warning' | 'info' | 'pending' | 'neutral' | 'brand';
  icon?: React.ReactNode;
  showIcon?: boolean;
  pulse?: boolean;
}

const StatusIndicator = React.forwardRef<HTMLSpanElement, StatusIndicatorProps>(
  ({ 
    className, 
    variant, 
    size, 
    style, 
    status,
    icon, 
    showIcon = true, 
    pulse = false,
    children, 
    ...props 
  }, ref) => {
    // Use status prop if provided, otherwise fall back to variant
    const computedVariant = status || variant;

    const getDefaultIcon = () => {
      switch (computedVariant) {
        case 'success':
          return <CheckCircle className="h-3 w-3" />;
        case 'error':
          return <XCircle className="h-3 w-3" />;
        case 'warning':
          return <AlertTriangle className="h-3 w-3" />;
        case 'info':
          return <Info className="h-3 w-3" />;
        case 'pending':
          return <Clock className="h-3 w-3" />;
        case 'brand':
          return <Info className="h-3 w-3" />;
        default:
          return <Minus className="h-3 w-3" />;
      }
    };

    return (
      <span
        ref={ref}
        className={cn(
          statusIndicatorVariants({ variant: computedVariant, size, style }),
          pulse && "animate-pulse",
          style === "dot" && "before:absolute before:left-2 before:top-1/2 before:-translate-y-1/2 before:h-2 before:w-2 before:rounded-full",
          style === "dot" && computedVariant === "success" && "before:bg-[hsl(var(--status-success))]",
          style === "dot" && computedVariant === "error" && "before:bg-[hsl(var(--status-error))]",
          style === "dot" && computedVariant === "warning" && "before:bg-[hsl(var(--status-warning))]",
          style === "dot" && computedVariant === "info" && "before:bg-[hsl(var(--status-info))]",
          style === "dot" && computedVariant === "pending" && "before:bg-muted-foreground",
          style === "dot" && computedVariant === "brand" && "before:bg-[hsl(var(--primary))]",
          className
        )}
        {...props}
      >
        {showIcon && style !== "dot" && (icon || getDefaultIcon())}
        {children}
      </span>
    );
  }
);
StatusIndicator.displayName = "StatusIndicator";

// Pre-built status indicators for common use cases
const InvoiceStatusIndicator = React.forwardRef<HTMLSpanElement, {
  status: 'draft' | 'pending' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}>(({ status, size = 'default', className }, ref) => {
  const statusConfig = {
    draft: { variant: 'neutral' as const, label: 'Draft' },
    pending: { variant: 'pending' as const, label: 'Pending' },
    sent: { variant: 'info' as const, label: 'Sent' },
    paid: { variant: 'success' as const, label: 'Paid' },
    overdue: { variant: 'error' as const, label: 'Overdue' },
    cancelled: { variant: 'neutral' as const, label: 'Cancelled' },
    refunded: { variant: 'warning' as const, label: 'Refunded' },
  };

  const config = statusConfig[status];

  return (
    <StatusIndicator
      ref={ref}
      variant={config.variant}
      size={size}
      className={className}
    >
      {config.label}
    </StatusIndicator>
  );
});
InvoiceStatusIndicator.displayName = "InvoiceStatusIndicator";

const PaymentStatusIndicator = React.forwardRef<HTMLSpanElement, {
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}>(({ status, size = 'default', className }, ref) => {
  const statusConfig = {
    pending: { variant: 'pending' as const, label: 'Pending', icon: <Clock className="h-3 w-3" />, pulse: false },
    processing: { variant: 'info' as const, label: 'Processing', icon: <CreditCard className="h-3 w-3" />, pulse: true },
    completed: { variant: 'success' as const, label: 'Completed', icon: <CheckCircle className="h-3 w-3" />, pulse: false },
    failed: { variant: 'error' as const, label: 'Failed', icon: <XCircle className="h-3 w-3" />, pulse: false },
    cancelled: { variant: 'neutral' as const, label: 'Cancelled', icon: <Minus className="h-3 w-3" />, pulse: false },
    refunded: { variant: 'warning' as const, label: 'Refunded', icon: <AlertTriangle className="h-3 w-3" />, pulse: false },
  };

  const config = statusConfig[status];

  return (
    <StatusIndicator
      ref={ref}
      variant={config.variant}
      size={size}
      icon={config.icon}
      pulse={config.pulse}
      className={className}
    >
      {config.label}
    </StatusIndicator>
  );
});
PaymentStatusIndicator.displayName = "PaymentStatusIndicator";

const UserRoleIndicator = React.forwardRef<HTMLSpanElement, {
  role: 'super_admin' | 'admin' | 'user' | 'viewer';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}>(({ role, size = 'default', className }, ref) => {
  const roleConfig = {
    super_admin: { variant: 'brand' as const, label: 'Super Admin', icon: <User className="h-3 w-3" /> },
    admin: { variant: 'info' as const, label: 'Admin', icon: <User className="h-3 w-3" /> },
    user: { variant: 'neutral' as const, label: 'User', icon: <User className="h-3 w-3" /> },
    viewer: { variant: 'neutral' as const, label: 'Viewer', icon: <User className="h-3 w-3" /> },
  };

  const config = roleConfig[role];

  return (
    <StatusIndicator
      ref={ref}
      variant={config.variant}
      size={size}
      icon={config.icon}
      className={className}
    >
      {config.label}
    </StatusIndicator>
  );
});
UserRoleIndicator.displayName = "UserRoleIndicator";

const PriorityIndicator = React.forwardRef<HTMLSpanElement, {
  priority: 'low' | 'medium' | 'high' | 'urgent';
  size?: 'sm' | 'default' | 'lg';
  showLabel?: boolean;
  className?: string;
}>(({ priority, size = 'default', showLabel = true, className }, ref) => {
  const priorityConfig = {
    low: { variant: 'neutral' as const, label: 'Low', icon: <Minus className="h-3 w-3" />, pulse: false },
    medium: { variant: 'info' as const, label: 'Medium', icon: <Info className="h-3 w-3" />, pulse: false },
    high: { variant: 'warning' as const, label: 'High', icon: <AlertTriangle className="h-3 w-3" />, pulse: false },
    urgent: { variant: 'error' as const, label: 'Urgent', icon: <AlertCircle className="h-3 w-3" />, pulse: true },
  };

  const config = priorityConfig[priority];

  return (
    <StatusIndicator
      ref={ref}
      variant={config.variant}
      size={size}
      icon={config.icon}
      pulse={config.pulse}
      className={className}
    >
      {showLabel && config.label}
    </StatusIndicator>
  );
});
PriorityIndicator.displayName = "PriorityIndicator";

// Activity status for timeline items
const ActivityStatusIndicator = React.forwardRef<HTMLSpanElement, {
  type: 'created' | 'updated' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  size?: 'sm' | 'default' | 'lg';
  className?: string;
}>(({ type, size = 'sm', className }, ref) => {
  const activityConfig = {
    created: { variant: 'info' as const, icon: <FileText className="h-3 w-3" /> },
    updated: { variant: 'neutral' as const, icon: <FileText className="h-3 w-3" /> },
    sent: { variant: 'info' as const, icon: <FileText className="h-3 w-3" /> },
    paid: { variant: 'success' as const, icon: <DollarSign className="h-3 w-3" /> },
    overdue: { variant: 'error' as const, icon: <AlertTriangle className="h-3 w-3" /> },
    cancelled: { variant: 'neutral' as const, icon: <XCircle className="h-3 w-3" /> },
  };

  const config = activityConfig[type];

  return (
    <StatusIndicator
      ref={ref}
      variant={config.variant}
      size={size}
      icon={config.icon}
      style="dot"
      className={className}
    >
      {/* No label for activity indicators - they're used in timelines */}
    </StatusIndicator>
  );
});
ActivityStatusIndicator.displayName = "ActivityStatusIndicator";

export {
  StatusIndicator,
  InvoiceStatusIndicator,
  PaymentStatusIndicator,
  UserRoleIndicator,
  PriorityIndicator,
  ActivityStatusIndicator,
  statusIndicatorVariants,
};
